
import { Component, Vue, Prop, Watch, Emit, Ref } from 'vue-property-decorator'
import type { LineConfig, DrawCanvasType, PointRingConfig } from '@xt/client/utils/canvas/index'
import { DrawCanvas } from '@xt/client/utils/canvas/index'

export type LineChartConfig = {
  // 线条宽度
  lineWidth: number
  // 线条颜色
  lineColor: string
  // 容器宽
  containerW: number
  // 容器高
  containerH: number
  // 坐标内圆半径
  innerRadius: number
  // 坐标外圆半径
  outerRadius: number
  // 坐标内圆颜色
  innerColor: string
  // 坐标外圆颜色
  outerColor: string
}

@Component({
  components: {}
})
export default class LineChart extends Vue {
  @Prop({ type: Array, default: () => [] }) data: number[]
  @Prop() config: LineChartConfig

  @Ref()
  canvas: HTMLCanvasElement

  drawCanvas: DrawCanvasType

  getPoints(): { x: number; y: number }[] {
    const { containerW: w, containerH: h, lineWidth } = this.config
    const padding = Math.floor(lineWidth / 2) + 3 // 两侧留白保证比线条圆角要大

    // 计算有效绘图区域（扣除两侧padding）
    const validWidth = w - padding * 2
    const validHeight = h - padding * 2

    // 数据映射参数
    const maxVal = Math.max(...this.data)
    const range = maxVal || 1 // 避免除以0
    const stepX = validWidth / (this.data.length - 1) // X轴步长

    if (this.data.length === 1) {
      return [
        {
          x: padding,
          y: padding
        },
        {
          x: w - padding,
          y: h - padding
        }
      ]
    }

    // 计算每个点的坐标
    return this.data.map((val, i) => {
      // X坐标：从左侧padding开始，按步长分布（确保两侧留空）
      const x = padding + i * stepX

      // Y坐标：映射到有效高度内（避免超出上下padding）
      const y = padding + validHeight - (val / range) * validHeight

      // 额外保护：确保Y坐标不超出容器（容错）
      return {
        x: x,
        y: Math.min(
          h - padding, // 最高不高于顶部padding
          Math.max(
            padding, // 最低不低于底部padding
            y
          )
        )
      }
    })
  }

  drawChart() {
    this.drawCanvas = new DrawCanvas({
      canvas: this.canvas,
      config: this.config
    })
    this.drawCanvas.initCanvas()
    this.drawCanvas.clearDraw()

    const points = this.getPoints()

    const { lineColor, lineWidth, innerRadius, outerRadius, innerColor, outerColor } = this.config
    const lineConfig: LineConfig = {
      points,
      lineColor,
      lineWidth
    }
    const pointRingConfig: PointRingConfig = {
      points,
      innerRadius,
      outerRadius,
      innerColor,
      outerColor
    }
    this.drawCanvas.drawLinePath(lineConfig)
    this.drawCanvas.drawPointRings(pointRingConfig)
  }

  created(): void {}

  mounted(): void {
    this.drawChart()
  }

  updated(): void {}

  beforeDestroy(): void {
    this.drawCanvas.clearDraw()
  }

  destroyed(): void {}
}
